import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Clock,
  Users,
  Mail,
  Phone,
  MapPin,
  Ticket,
  Anchor,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { useLanguage } from '../hooks/useLanguage';
import { getPaymentTraceSessionId, paymentTrace, paymentTraceHeaders } from '../lib/paymentTrace';

const localeForLanguage = (language) => {
  if (language === 'fr') return 'fr-FR';
  if (language === 'es') return 'es-ES';
  return 'en-GB';
};

const formatBookingDate = (dateStr, language) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString(localeForLanguage(language), {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatMoney = (amount, currency = 'EUR', language) => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat(localeForLanguage(language), {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${currency || 'EUR'}`;
  }
};

const DetailRow = ({ icon: Icon, label, value, highlight = false }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p
          className={`mt-0.5 text-base break-words ${
            highlight ? 'font-bold text-primary-950' : 'font-semibold text-gray-900'
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
};

const BookingConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const bookingId = searchParams.get('booking_id');
  const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    document.body.classList.add('header-dark-wrapper');
    const style = document.createElement('style');
    style.innerHTML = `
      .header-dark-wrapper header {
        background-color: #0B1F3F !important;
      }
      .header-dark-wrapper header nav a,
      .header-dark-wrapper header > div > div > div > button {
        color: white !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove('header-dark-wrapper');
      style.remove();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadConfirmation = async () => {
      const res = await axios.get(`${API_URL}/api/bookings/${bookingId}/confirmation`, {
        headers: paymentTraceHeaders(),
      });
      if (cancelled) return;
      setConfirmation(res.data);
      setStatus('success');
      paymentTrace('confirmation_page.success', { booking_id: bookingId });
    };

    const finalize = async () => {
      paymentTrace('confirmation_page.start', {
        booking_id: bookingId,
        redirect_status: redirectStatus,
        has_pi_secret: Boolean(paymentIntentClientSecret),
        session_id: getPaymentTraceSessionId(),
      });

      if (!bookingId) {
        setStatus('error');
        setMessage(t('bookingConfirmation.missingBooking'));
        return;
      }

      if (redirectStatus === 'failed') {
        setStatus('error');
        setMessage(t('bookingConfirmation.paymentFailed'));
        return;
      }

      try {
        if (paymentIntentClientSecret) {
          const configRes = await axios.get(`${API_URL}/api/stripe/config`);
          const stripe = await loadStripe(configRes.data.publishable_key);
          if (!stripe) {
            throw new Error('Stripe failed to load');
          }
          const { paymentIntent, error } = await stripe.retrievePaymentIntent(
            paymentIntentClientSecret
          );
          if (error) {
            throw error;
          }
          if (paymentIntent?.status !== 'succeeded') {
            setStatus('error');
            setMessage(t('bookingConfirmation.paymentIncomplete'));
            return;
          }
        }

        try {
          await loadConfirmation();
          return;
        } catch (getErr) {
          if (getErr?.response?.status !== 403) {
            throw getErr;
          }
        }

        const confirmRes = await axios.post(
          `${API_URL}/api/bookings/${bookingId}/confirm-wowbooking`,
          {},
          { headers: paymentTraceHeaders() }
        );
        if (cancelled) return;
        if (confirmRes.data?.confirmation) {
          setConfirmation(confirmRes.data.confirmation);
          setStatus('success');
          return;
        }
        await loadConfirmation();
      } catch (err) {
        if (cancelled) return;
        console.error('Booking confirmation failed:', err);
        paymentTrace('confirmation_page.error', {
          booking_id: bookingId,
          status: err?.response?.status,
          detail: err?.response?.data?.detail,
        });
        const detail = err?.response?.data?.detail;
        setStatus('error');
        setMessage(
          typeof detail === 'string' && detail
            ? detail
            : t('bookingConfirmation.confirmError')
        );
      }
    };

    finalize();
    return () => {
      cancelled = true;
    };
  }, [API_URL, bookingId, paymentIntentClientSecret, redirectStatus, t]);

  const customerName = confirmation
    ? [confirmation.customer?.first_name, confirmation.customer?.last_name]
        .filter(Boolean)
        .join(' ')
    : '';

  const timeRange =
    confirmation?.start_time && confirmation?.end_time
      ? `${confirmation.start_time} – ${confirmation.end_time}`
      : confirmation?.start_time || null;

  const hasDiscount =
    confirmation &&
    Number(confirmation.discount_amount) > 0 &&
    Number(confirmation.amount_before_discount) > Number(confirmation.total_ttc);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SeoHead
        title={`${t('bookingConfirmation.title')} | Wownautic`}
        description={t('bookingConfirmation.success')}
        canonicalPath="/booking-confirmation"
        noindex
      />
      <Header />

      <main className="flex-1 pt-28 pb-16">
        {status === 'loading' && (
          <div className="flex flex-col items-center justify-center min-h-[50vh] px-4 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
            <p className="text-gray-600 text-lg">{t('bookingConfirmation.processing')}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="max-w-lg mx-auto px-4 py-12 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary-950 mb-2">
              {t('bookingConfirmation.errorTitle')}
            </h1>
            <p className="text-gray-600 mb-8">{message}</p>
            <Button asChild className="bg-primary-600 hover:bg-primary-700">
              <Link to="/">{t('bookingConfirmation.backHome')}</Link>
            </Button>
          </div>
        )}

        {status === 'success' && confirmation && (
          <div className="max-w-2xl mx-auto px-4">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-5">
                <CheckCircle2 className="w-11 h-11 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-primary-950 mb-2">
                {t('bookingConfirmation.title')}
              </h1>
              <p className="text-lg text-gray-600 max-w-md mx-auto">
                {t('bookingConfirmation.subtitle')}
              </p>
            </div>

            {confirmation.reference && (
              <div className="mb-6 rounded-2xl bg-gradient-to-r from-primary-950 to-primary-800 text-white p-6 text-center shadow-lg">
                <p className="text-sm uppercase tracking-widest text-primary-200 mb-1">
                  {t('bookingConfirmation.referenceLabel')}
                </p>
                <p className="text-2xl md:text-3xl font-mono font-bold tracking-wide">
                  {confirmation.reference}
                </p>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
              <div className="bg-primary-50 px-6 py-4 border-b border-primary-100">
                <h2 className="text-lg font-bold text-primary-950 flex items-center gap-2">
                  <Anchor className="w-5 h-5 text-primary-600" />
                  {t('bookingConfirmation.detailsTitle')}
                </h2>
              </div>
              <div className="px-6 py-2">
                <DetailRow
                  icon={Ticket}
                  label={t('bookingConfirmation.experience')}
                  value={confirmation.offer_title}
                  highlight
                />
                <DetailRow
                  icon={Calendar}
                  label={t('bookingConfirmation.date')}
                  value={formatBookingDate(confirmation.booking_date, language)}
                />
                <DetailRow
                  icon={Clock}
                  label={t('bookingConfirmation.time')}
                  value={timeRange}
                />
                <DetailRow
                  icon={Users}
                  label={t('bookingConfirmation.participants')}
                  value={confirmation.participants}
                />
                {confirmation.resource_name && (
                  <DetailRow
                    icon={Anchor}
                    label={t('bookingConfirmation.equipment')}
                    value={confirmation.resource_name}
                  />
                )}
                <DetailRow icon={Mail} label={t('bookingConfirmation.guest')} value={customerName} />
                <DetailRow
                  icon={Mail}
                  label={t('bookingConfirmation.email')}
                  value={confirmation.customer?.email}
                />
                {confirmation.customer?.phone && (
                  <DetailRow
                    icon={Phone}
                    label={t('bookingConfirmation.phone')}
                    value={confirmation.customer.phone}
                  />
                )}
              </div>
              <div className="px-6 py-5 bg-gray-50 border-t border-gray-100">
                {hasDiscount && (
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{t('bookingConfirmation.subtotal')}</span>
                    <span>
                      {formatMoney(
                        confirmation.amount_before_discount,
                        confirmation.currency,
                        language
                      )}
                    </span>
                  </div>
                )}
                {hasDiscount && confirmation.coupon_code && (
                  <div className="flex justify-between text-sm text-green-700 mb-2">
                    <span>
                      {t('bookingConfirmation.discount')} ({confirmation.coupon_code})
                    </span>
                    <span>
                      −
                      {formatMoney(
                        confirmation.discount_amount,
                        confirmation.currency,
                        language
                      )}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-base font-semibold text-gray-900">
                    {t('bookingConfirmation.totalPaid')}
                  </span>
                  <span className="text-2xl font-bold text-primary-600">
                    {formatMoney(confirmation.total_ttc, confirmation.currency, language)}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-primary-200 bg-white p-6 mb-6 shadow-sm">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary-700" />
                </div>
                <div>
                  <h3 className="font-bold text-primary-950 mb-1">
                    {t('bookingConfirmation.meetingTitle')}
                  </h3>
                  <p className="text-gray-700">{t('bookingConfirmation.meetingPoint')}</p>
                  <p className="text-sm text-gray-500 mt-2">{t('bookingConfirmation.meetingHint')}</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-primary-50 border border-primary-100 px-5 py-4 text-center text-sm text-primary-900 mb-8">
              {t('bookingConfirmation.emailNotice', {
                email: confirmation.customer?.email || '',
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary-600 hover:bg-primary-700">
                <Link to="/">{t('bookingConfirmation.backHome')}</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-300">
                <Link to="/contact">{t('bookingConfirmation.contactUs')}</Link>
              </Button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BookingConfirmationPage;
